import React, { useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { trackEvent } from '../../services/analyticsService';
import { startRecipeAuthoring } from '../../services/recipeDraftService';
import { useAppShell } from '../../contexts/AppShellContext';
import KitchenLayout from '../Navigation/KitchenLayout';
import { PlanLimitDialog } from '../Billing/PlanLimitPrompt';
import { BillingLimit, billingLimitFromError } from '../../utils/billingLimit';
import MarkupEditor from './MarkupEditor';
import './RecipeNew.css';

type StarterIdeaId = 'email' | 'report' | 'watch';
type AnnotationId = '1' | '2' | '3' | '4';

interface StarterIdea {
  id: StarterIdeaId;
  title: string;
  description: string;
  content: string;
  icon: React.ReactNode;
}

interface AnnotationArrow {
  id: AnnotationId;
  path: string;
  pointsRight: boolean;
}

const SAMPLE_RECIPE = [
  'Every weekday at 8:30,',
  'check new customer emails,',
  'summarize what needs attention,',
  'and email the summary to me.',
].join('\n');

const ANNOTATIONS: Array<{ id: AnnotationId; title: string; description: string }> = [
  { id: '1', title: 'Pick the moment', description: 'A time, an event, or whenever you choose.' },
  { id: '2', title: 'Choose what it uses', description: 'Your email, calendar, files, or another app.' },
  { id: '3', title: 'Say what to do', description: 'Use ordinary words. You can change it anytime.' },
  { id: '4', title: 'Choose the result', description: 'Decide what you receive and where it goes.' },
];

const STARTER_IDEAS: StarterIdea[] = [
  {
    id: 'email',
    title: 'Keep up with my inbox',
    description: 'Get a helpful summary instead of checking every message.',
    content: 'Every weekday morning, summarize my unread customer emails and send the summary to me.',
    icon: '✉',
  },
  {
    id: 'report',
    title: 'Make a weekly update',
    description: 'Bring the week’s important activity into one simple note.',
    content: 'Every Friday afternoon, create a report of this week’s important activity and send it to my team.',
    icon: '▥',
  },
  {
    id: 'watch',
    title: 'Tell me when something changes',
    description: 'Keep an eye on a page and let me know when it’s different.',
    content: 'Check a webpage every morning and message me only when something has changed since yesterday.',
    icon: '⌁',
  },
];

const RecipeNew: React.FC = () => {
  const navigate = useNavigate();
  const creationStartedRef = useRef(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const explainerRef = useRef<HTMLElement>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  const tipRefs = useRef<Partial<Record<AnnotationId, HTMLButtonElement>>>({});
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [planLimit, setPlanLimit] = useState<BillingLimit | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [creatingStarter, setCreatingStarter] = useState<StarterIdeaId | null>(null);
  const [activeAnnotation, setActiveAnnotation] = useState<AnnotationId | null>(null);
  const [showAiComposer, setShowAiComposer] = useState(false);
  const [annotationArrows, setAnnotationArrows] = useState<AnnotationArrow[]>([]);
  const [arrowCanvas, setArrowCanvas] = useState({ width: 0, height: 0 });
  const { skin } = useAppShell();
  const isYolo = skin === 'yolo';

  const createRecipe = async (
    recipeDescription: string,
    creationFlow: 'conversational' | 'starter_recipe',
    starterId?: StarterIdeaId,
  ) => {
    const cleanDescription = recipeDescription.trim();
    if (!cleanDescription || creationStartedRef.current) return;

    creationStartedRef.current = true;
    setIsCreating(true);
    setCreatingStarter(starterId || null);
    setError(null);
    try {
      const result = await startRecipeAuthoring(cleanDescription);
      trackEvent('recipe_created', {
        recipe_id: result.recipe.id,
        content_length: cleanDescription.length,
        creation_flow: creationFlow,
        ...(starterId ? { starter_recipe: starterId } : {}),
      });
      navigate(`/recipes/${result.recipe.id}/edit`, {
        replace: creationFlow === 'conversational',
        state: { initialDraftJobId: result.draftJobId },
      });
    } catch (reason: any) {
      creationStartedRef.current = false;
      setIsCreating(false);
      setCreatingStarter(null);
      const nextPlanLimit = billingLimitFromError(reason);
      if (nextPlanLimit) {
        setPlanLimit(nextPlanLimit);
        return;
      }
      setError(reason?.response?.data?.message || reason?.message || 'Could not start this recipe');
      if (creationFlow === 'conversational') requestAnimationFrame(() => inputRef.current?.focus());
    }
  };

  const openAiComposer = () => {
    setShowAiComposer(true);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  useLayoutEffect(() => {
    const explainer = explainerRef.current;
    const editor = editorRef.current;
    if (!explainer || !editor) return;

    let frame = 0;
    const updateArrows = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (window.innerWidth <= 1120) {
          setAnnotationArrows([]);
          return;
        }

        const explainerRect = explainer.getBoundingClientRect();
        const lines = Array.from(editor.querySelectorAll<HTMLElement>('.cm-line'));
        if (lines.length < ANNOTATIONS.length) return;

        const paths = ANNOTATIONS.flatMap((annotation, index) => {
          const tip = tipRefs.current[annotation.id];
          const line = lines[index];
          if (!tip || !line) return [];

          const tipRect = tip.getBoundingClientRect();
          const lineRect = line.getBoundingClientRect();
          const textRange = document.createRange();
          textRange.selectNodeContents(line);
          const textRect = textRange.getBoundingClientRect();
          const tipIsLeft = tipRect.left < explainerRect.left + explainerRect.width / 2;
          const startX = (tipIsLeft ? tipRect.right + 6 : tipRect.left - 6) - explainerRect.left;
          const startY = tipRect.top + tipRect.height / 2 - explainerRect.top;
          const endX = (tipIsLeft ? textRect.left - 8 : textRect.right + 8) - explainerRect.left;
          const endY = lineRect.top + lineRect.height / 2 - explainerRect.top;
          const direction = tipIsLeft ? 1 : -1;
          const neckX = endX - direction * 20;
          const horizontalDistance = neckX - startX;
          const firstControlX = startX + horizontalDistance * (tipIsLeft ? 0.42 : 0.3);
          const secondControlX = neckX - direction * 26;

          return [{
            id: annotation.id,
            path: `M ${startX} ${startY} C ${firstControlX} ${startY}, ${secondControlX} ${endY}, ${neckX} ${endY} L ${endX} ${endY}`,
            pointsRight: tipIsLeft,
          }];
        });

        setArrowCanvas({ width: explainerRect.width, height: explainerRect.height });
        setAnnotationArrows(paths);
      });
    };

    const resizeObserver = new ResizeObserver(updateArrows);
    resizeObserver.observe(explainer);
    resizeObserver.observe(editor);
    window.addEventListener('resize', updateArrows);
    updateArrows();

    return () => {
      cancelAnimationFrame(frame);
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateArrows);
    };
  }, []);

  return (
    <KitchenLayout title="Welcome" className={isYolo ? 'bg-[#f7f3e8]' : 'bg-gray-50'}>
      {planLimit && <PlanLimitDialog limit={planLimit} onClose={() => setPlanLimit(null)} />}
      <main className={`recipe-welcome-main min-w-0 flex-1 overflow-y-auto ${isYolo ? 'bg-[#f7f3e8]' : 'bg-gray-50'}`}>
        <div className="mx-auto w-full max-w-6xl px-5 py-10 sm:px-7 lg:px-10 lg:py-12">
          <header className="mx-auto max-w-3xl text-center">
            <h1 className={`text-3xl font-semibold tracking-[-0.04em] sm:text-4xl lg:text-5xl ${isYolo ? 'text-stone-900' : 'text-gray-950'}`}>
              Use everyday English to write and run automation recipes.
            </h1>
          </header>

          <section ref={explainerRef} className="recipe-welcome-explainer" aria-label="Anatomy of a recipe">
            <div ref={editorRef} className={`recipe-welcome-editor ${activeAnnotation ? `recipe-welcome-editor--active-${activeAnnotation}` : ''}`}>
              <div className="recipe-welcome-editor__title">This is what a recipe looks like</div>
              <div className="recipe-welcome-editor__body">
                <MarkupEditor id="sample-recipe" value={SAMPLE_RECIPE} onChange={() => undefined} readOnly />
                {ANNOTATIONS.map((annotation, index) => (
                  <button
                    key={annotation.id}
                    type="button"
                    className={`recipe-welcome-marker recipe-welcome-marker--${index + 1}`}
                    aria-label={`Explanation ${annotation.id}: ${annotation.title}`}
                    onMouseEnter={() => setActiveAnnotation(annotation.id)}
                    onMouseLeave={() => setActiveAnnotation(null)}
                    onFocus={() => setActiveAnnotation(annotation.id)}
                    onBlur={() => setActiveAnnotation(null)}
                  >
                    {annotation.id}
                  </button>
                ))}
              </div>
            </div>

            {annotationArrows.length > 0 && (
              <svg
                className="recipe-welcome-arrows"
                viewBox={`0 0 ${arrowCanvas.width} ${arrowCanvas.height}`}
                preserveAspectRatio="none"
                aria-hidden="true"
              >
                <defs>
                  <marker id="recipe-welcome-arrowhead-right" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="0" markerUnits="strokeWidth">
                    <path d="M 0 0 L 7 3.5 L 0 7" />
                  </marker>
                  <marker id="recipe-welcome-arrowhead-left" markerWidth="7" markerHeight="7" refX="1" refY="3.5" orient="180" markerUnits="strokeWidth">
                    <path d="M 0 0 L 7 3.5 L 0 7" />
                  </marker>
                </defs>
                {annotationArrows.map(arrow => (
                  <path
                    key={arrow.id}
                    className="recipe-welcome-arrow"
                    d={arrow.path}
                    markerEnd={`url(#recipe-welcome-arrowhead-${arrow.pointsRight ? 'right' : 'left'})`}
                  />
                ))}
              </svg>
            )}

            <div className="recipe-welcome-tips">
              {ANNOTATIONS.map(annotation => (
                <button
                  key={annotation.id}
                  ref={element => { tipRefs.current[annotation.id] = element || undefined; }}
                  type="button"
                  className={`recipe-welcome-tip recipe-welcome-tip--${annotation.id} ${activeAnnotation === annotation.id ? 'is-active' : ''}`}
                  onMouseEnter={() => setActiveAnnotation(annotation.id)}
                  onMouseLeave={() => setActiveAnnotation(null)}
                  onFocus={() => setActiveAnnotation(annotation.id)}
                  onBlur={() => setActiveAnnotation(null)}
                >
                  <span className="recipe-welcome-tip__number">{annotation.id}</span>
                  <span>
                    <strong>{annotation.title}</strong>
                    <small>{annotation.description}</small>
                  </span>
                </button>
              ))}
            </div>
          </section>

          <section className={`mt-9 border-t pt-8 text-center ${isYolo ? 'border-[#e2dacb]' : 'border-gray-200'}`} aria-labelledby="starter-ideas-title">
            <h2 id="starter-ideas-title" className={`text-2xl font-semibold tracking-tight ${isYolo ? 'text-stone-900' : 'text-gray-950'}`}>Start with an idea</h2>
            <p className={`mt-2 text-sm ${isYolo ? 'text-stone-500' : 'text-gray-500'}`}>
              Choose something familiar. You can adjust every detail before it runs.
            </p>

            <div className="mt-5 grid gap-3 text-left md:grid-cols-3">
              {STARTER_IDEAS.map(idea => (
                <button
                  key={idea.id}
                  type="button"
                  onClick={() => void createRecipe(idea.content, 'starter_recipe', idea.id)}
                  disabled={isCreating}
                  className={`group min-h-32 rounded-2xl border p-4 text-left shadow-[0_8px_24px_rgba(83,63,32,0.05)] transition hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 disabled:cursor-wait disabled:opacity-60 ${isYolo ? 'border-[#ded2bf] bg-[#fffaf1] hover:border-amber-400 focus-visible:ring-amber-500' : 'border-gray-200 bg-white hover:border-blue-300 focus-visible:ring-blue-500'}`}
                >
                  <span className={`flex h-9 w-9 items-center justify-center rounded-xl text-lg ${isYolo ? 'bg-amber-100 text-amber-800' : 'bg-blue-50 text-blue-700'}`} aria-hidden="true">{idea.icon}</span>
                  <span className={`mt-3 block text-sm font-semibold ${isYolo ? 'text-stone-900' : 'text-gray-900'}`}>
                    {creatingStarter === idea.id ? 'Creating your recipe…' : idea.title}
                  </span>
                  <span className={`mt-1 block text-xs leading-5 ${isYolo ? 'text-stone-500' : 'text-gray-500'}`}>{idea.description}</span>
                </button>
              ))}
            </div>

            <div className={`mt-5 rounded-2xl px-4 py-4 ${isYolo ? 'bg-[#eee9dd]' : 'bg-gray-100'}`}>
              {!showAiComposer ? (
                <div className={`flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-sm ${isYolo ? 'text-stone-600' : 'text-gray-600'}`}>
                  <span aria-hidden="true">✦</span>
                  <span>Not sure where to start?</span>
                  <button
                    type="button"
                    onClick={openAiComposer}
                    className={`font-semibold underline decoration-1 underline-offset-4 ${isYolo ? 'text-amber-900 hover:text-stone-950' : 'text-blue-700 hover:text-blue-900'}`}
                  >
                    Describe what you need and let Chef draft a recipe
                  </button>
                </div>
              ) : (
                <div className="mx-auto max-w-3xl text-left">
                  <label htmlFor="recipe-description" className={`block text-sm font-semibold ${isYolo ? 'text-stone-800' : 'text-gray-800'}`}>
                    What would you like to automate?
                  </label>
                  <div className={`mt-2 rounded-2xl border bg-white p-2 shadow-sm ${isYolo ? 'border-[#dfd1b9]' : 'border-gray-200'}`}>
                    <textarea
                      id="recipe-description"
                      ref={inputRef}
                      value={description}
                      onChange={event => setDescription(event.target.value)}
                      onKeyDown={event => {
                        if (event.key === 'Enter' && !event.shiftKey) {
                          event.preventDefault();
                          void createRecipe(description, 'conversational');
                        }
                      }}
                      rows={3}
                      maxLength={20_000}
                      disabled={isCreating}
                      placeholder="For example: Every weekday morning, summarize my unread customer emails and send the summary to me."
                      className="min-h-[86px] w-full resize-none rounded-xl border-0 bg-white px-3 py-2.5 text-sm text-gray-900 outline-none placeholder:text-gray-400 focus:ring-0 disabled:bg-gray-50"
                    />
                    <div className="flex items-center justify-between gap-3 border-t border-gray-100 px-1 pb-1 pt-2">
                      <span className="hidden text-xs text-gray-400 sm:block">Enter to send · Shift+Enter for a new line</span>
                      <button
                        type="button"
                        onClick={() => void createRecipe(description, 'conversational')}
                        disabled={!description.trim() || isCreating}
                        className={`ml-auto rounded-xl px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition disabled:cursor-not-allowed disabled:bg-gray-300 ${isYolo ? 'bg-amber-600 hover:bg-amber-700' : 'bg-blue-600 hover:bg-blue-700'}`}
                      >
                        {isCreating && !creatingStarter ? 'Drafting recipe…' : 'Draft my recipe'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {error && <p className="mt-4 text-sm text-red-600" role="alert">{error}</p>}
          </section>
        </div>
      </main>
    </KitchenLayout>
  );
};

export default RecipeNew;
